import React, { useEffect, useState } from 'react';
import { GOOGLE_MAPS_API_KEY } from '../../lib/map/constants';
import { useTranslation } from 'react-i18next';

// We can't redeclare google property - only declare dynamic properties
// that don't conflict with the declaration in useLocationManager.ts
declare global {
  interface Window {
    // Dynamic callback property for script loading
    [key: `googleMapsPlacesCallback_${string}`]: () => void;
  }
}

// Define a type for Google Maps with Places API for type assertions
interface GoogleMapsWithPlaces {
  places: any;
  importLibrary: (name: string) => Promise<any>;
}

// Load Google Maps API and ensure Places library is also loaded
const loadGoogleMapsWithPlaces = (): Promise<void> => {
  return new Promise((resolve, reject) => {
    // If already loaded, return immediately
    if (window.google && window.google.maps && 
        'places' in window.google.maps) {
      console.log('Google Maps API with Places already loaded');
      return resolve();
    }
    
    // If Google Maps loaded but Places missing
    if (window.google && window.google.maps && 
        !('places' in window.google.maps)) {
      console.log('Google Maps loaded but Places missing, trying to import Places');
      
      // Check if importLibrary method exists
      if (window.google.maps && 'importLibrary' in window.google.maps) {
        // Get the importLibrary function
        const importLibFn = (window.google.maps as any).importLibrary;
        if (typeof importLibFn === 'function') {
          importLibFn('places')
            .then(() => {
              console.log('Places library imported successfully!');
              resolve();
            })
            .catch((err: Error) => {
              console.error('Failed to import Places library:', err);
              reject(err);
            });
          return;
        }
      }
    }
    
    // If not loaded, create and add script
    const callbackName = 'googleMapsPlacesCallback_' + Math.random().toString(36).substring(2, 15);
    (window as any)[callbackName] = () => {
      console.log('Google Maps API loaded via script with Places library');
      
      // Double check Places is loaded
      if (!window.google?.maps || !('places' in window.google.maps)) {
        console.error('Places library still missing after loading!');
        reject(new Error('Places library failed to load'));
      } else {
        delete (window as any)[callbackName];
        resolve();
      }
    };
    
    // Create script element
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places,geometry&callback=${callbackName}&v=weekly`;
    script.async = true;
    script.defer = true;
    
    script.onerror = (error) => {
      console.error('Error loading Google Maps script:', error);
      delete (window as any)[callbackName];
      reject(error);
    };
    
    // Add to page
    document.head.appendChild(script);
  });
};

interface GoogleMapsLoaderProps {
  onLoad?: () => void;
  onError?: (error: Error) => void;
  children: React.ReactNode;
}

const GoogleMapsLoader: React.FC<GoogleMapsLoaderProps> = ({ 
  onLoad, 
  onError,
  children 
}) => {
  const { t } = useTranslation();
  const [isLoaded, setIsLoaded] = useState(false);
  const [loadError, setLoadError] = useState<Error | null>(null);
  
  useEffect(() => {
    loadGoogleMapsWithPlaces()
      .then(() => {
        setIsLoaded(true);
        if (onLoad) onLoad();
      })
      .catch(error => {
        console.error('GoogleMapsLoader error:', error);
        setLoadError(error instanceof Error ? error : new Error(String(error)));
        if (onError) onError(error instanceof Error ? error : new Error(String(error)));
      });
  }, [onLoad, onError]);
  
  // If there's an error, show error message
  if (loadError) {
    return (
      <div className="text-red-500 p-4 bg-red-50 rounded-lg">
        <p className="font-bold">{t('components.map.mapLoadError')}</p>
        <p>{t('components.map.refreshAndTryAgain')}</p>
        <p className="text-xs mt-2 text-gray-500">{t('components.map.errorDetails')}: {loadError.message}</p>
      </div>
    );
  }
  
  // If loading, show loading indicator
  if (!isLoaded) {
    return (
      <div className="flex items-center justify-center p-4">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-gray-900"></div>
        <p className="ml-2">{t('components.map.loadingMap')}</p>
      </div>
    );
  }
  
  // Load successful, render children
  return <>{children}</>;
};

export default GoogleMapsLoader; 